import { useEffect, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ActionModal, Button, Field, EmptyState, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useSettlement,
  useUpdateSettlement,
  useFinalizeSettlement,
  useFinancials,
  useUpdateFinancials,
  useShowLinkedDays,
  settlementStatusLabel,
} from "../../app/hooks/useMoney.js";
import { useExpenses, useUpdateExpenses } from "../../app/hooks/useShows.js";
import { useTravel } from "../../app/hooks/useTravel.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { ReopenShowButton } from "./ReopenShowModal.js";
import { ExpenseLinesList, type ExpenseLineDraft } from "./ExpenseLinesList.js";
import styles from "./SettlementPanel.module.scss";

// ART-247/248 + ART-290 — ONE net to artist, post-expense, merch counted as income. The server computes
// `netToArtistCents = headlineTake + merchGross − merchCut − §09 expenses` and serves the line items
// (`headlineTakeCents`, `merchGrossCents`, `merchCutOwedCents`, `expensesTotalCents`) so the left stack
// reconciles: headlineTake + merch − merchCut − expenses = net. This panel never re-derives the net. Ryan's redesign:
// edits AUTOSAVE (no Save button), no merch section (edited in the show editor), Splits → Terms, and ONE tall
// Expenses box listing every expense.
//
// TWO STATUS AXES (Ryan, ART-241): a SHOW is "open" or "completed" (its lifecycle dot, in the ShowPreview left
// rail — set by Outstanding Payments' "Close out show"); a SETTLEMENT is "in progress" or "settled" WITH THE
// VENUE — this panel's chip, the worksheet's own `status`. The venue axis is toggled by "Mark settled" (this
// card's button AND the mobile DOS card, both `useFinalizeSettlement`) and undone by the audited Reopen. The
// chip reflects the VENUE axis alone; the panel goes read-only (deal locked) once the worksheet is settled —
// and closing a show out finalizes its worksheet too, so a completed show is always venue-settled as well.

type SettlementDto = components["schemas"]["SettlementDto"];
type SettlementRead = components["schemas"]["SettlementRead"];
type FinancialsRead = components["schemas"]["FinancialsRead"];
type ExpenseLineDto = components["schemas"]["ExpenseLineDto"];
type TravelItemDto = components["schemas"]["TravelItemDto"];
type ShowMemberPayLineDto = components["schemas"]["ShowMemberPayLineDto"];
type LinkedDayLine = components["schemas"]["LinkedDayLine"];

const SAVE_DEBOUNCE_MS = 600;

function money(cents: number | null | undefined): string {
  const v = (cents ?? 0) / 100;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function centsToStr(c: number | null | undefined): string { return c == null ? "" : (c / 100).toFixed(2); }
function strToCents(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Math.round(Number(t) * 100); }
function strToInt(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Math.round(Number(t)); }

export interface SettlementPanelProps {
  showId: string;
  /** The SHOW's lifecycle status (`hold|confirmed|settled|…`). A show is "settled" for this panel when its
   *  lifecycle dot says so — set by Outstanding Payments' "Close out show" (ART‑241/248) — OR its worksheet is
   *  finalized (the mobile "Mark settled" path). Either flips the panel read‑only + surfaces "Reopen show". */
  showStatus?: string | null;
  /** `xs` header controls — DESKTOP ONLY (30px, under the 44px touch min; the mobile DOS card is its own
   *  component). The default stays `sm`; the desktop show dialog opts down. */
  dense?: boolean;
}

/**
 * The settlement panel on the show preview — where settlement is READ and edited (settling itself now happens in
 * Outstanding Payments, ART-248). MONEY-GATED: money:full/per_show only; no-money and money:per_person seats
 * render nothing (the whole panel is a separate money endpoint the client only calls when the capability is
 * held — the A3 rule). Comps derive LIVE from the guest list; every mutation refetches via the
 * `settlement-updated` signal on `show:{id}`.
 */
export function SettlementPanel({ showId, showStatus, dense }: SettlementPanelProps) {
  // Capability reads are unconditional (Rules of Hooks); the branch is a pure boolean.
  const canFull = useCapability("money:full");
  const canPerShow = useCapability("money:per_show");
  if (canFull || canPerShow) return <FullSettlement showId={showId} showStatus={showStatus} canFull={canFull} dense={dense} />;
  return null;   // no settle capability → the settlement panel is absent by construction
}

// ---- money:full | money:per_show — the full settlement ---------------------------------------------------

function FullSettlement({ showId, showStatus, canFull, dense }: { showId: string; showStatus?: string | null; canFull: boolean; dense?: boolean }) {
  const query = useSettlement(showId, true);
  const update = useUpdateSettlement();
  // The DEAL — guarantee, door split % and its threshold — is READ off the settlement (the server stamps it
  // there from the plan) but WRITTEN to financials, its one authority.
  const plan = useFinancials(showId, true);
  const updatePlan = useUpdateFinancials();
  // ART-240 — the off/travel days attributed to this show + their cost, listed in the Expenses box.
  const linkedDays = useShowLinkedDays(showId, true);
  const expensesQ = useExpenses(showId);
  const travelQ = useTravel(showId);

  const [expensesOpen, setExpensesOpen] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);

  // The latest server reads, held in refs so a debounced autosave always fires with the CURRENT versions — a
  // 409 otherwise lands on the next keystroke after a refetch bumps them (the DosSettlementCard pattern).
  const readRef = useRef<SettlementRead | undefined>(undefined);
  readRef.current = query.data;
  const planRef = useRef<FinancialsRead | undefined>(undefined);
  planRef.current = plan.data;

  const d = query.data?.data;
  const finalize = useFinalizeSettlement();
  // The worksheet's own VENUE-settle state — the chip axis, and what "Mark settled" flips. It also LOCKS the
  // deal (finalize's settle-lock), so it drives read-only + the Reopen button. A closed-out (completed) show
  // has its worksheet finalized too, so `showStatus === "settled"` is folded in as a belt-and-braces lock: the
  // two agree in every reachable state, and this keeps a completed show read-only even in the edge where it
  // carried no worksheet. The CHIP, by contrast, reads the worksheet status ALONE (the venue axis) — never the
  // lifecycle — so it is independent of the show's open/completed dot.
  const settled = showStatus === "settled" || d?.status === "settled";

  // AUTOSAVE — no Save button (Ryan). Debounced ~600ms after the last edit: the DEAL goes to financials (only
  // when a deal field changed — it bumps the plan's e_ver and is audited), then the settlement scalars go to the
  // settlement. Both are full-replace PUTs, so every dormant field is round-tripped from the stored read (a
  // door-count edit must not wipe the band's hotel/agent% or the deposit's received stamp). Never fires when
  // settled (the fields are read-only). Declared before any early return (Rules of Hooks).
  useEffect(() => {
    if (form === null || settled) return;
    const t = setTimeout(() => { void persist(form); }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // `persist` closes over stable refs + mutations; re-run only when the edited values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, settled]);

  async function persist(f: FormState) {
    try {
      await saveDeal(f);
    } catch {
      return;   // useUpdateFinancials toasted; a failed deal must not half-save as a settlement
    }
    const cur = readRef.current;
    update.mutate({ showId, version: cur?.version ?? 0, body: toScalars(f) });
  }

  /**
   * The DEAL half of an autosave: guarantee / door split % / threshold live on `show_finances`, so they go out
   * as a financials PUT — and only when one actually CHANGED, because that PUT bumps the plan's e_ver and is
   * audited. A whole-document write, so every field this panel does not show is round-tripped from the stored
   * plan (omitting them would wipe the band's hotel/flights/agent%). `doorSplit` is derived from whether a % is
   * present, exactly as the editor's checkbox drives it.
   */
  async function saveDeal(f: FormState) {
    const guaranteeCents = strToCents(f.guarantee);
    const pct = f.doorSplitPct.trim() === "" ? undefined : Number(f.doorSplitPct);
    const doorSplit = pct !== undefined;
    const doorSplitAfterCents = doorSplit ? strToCents(f.doorSplitAfter) : undefined;
    const stored = planRef.current?.data;
    const unchanged =
      (guaranteeCents ?? null) === (stored?.guaranteeCents ?? null)
      && (pct ?? null) === (stored?.doorSplitPct ?? null)
      && (doorSplitAfterCents ?? null) === (stored?.doorSplitAfterCents ?? null);
    if (unchanged) return;
    if (!stored) {
      pushToast("Couldn’t save the guarantee or door split — the show’s financials didn’t load. Try again.");
      return;
    }
    await updatePlan.mutateAsync({
      showId,
      version: planRef.current?.version ?? 0,
      body: {
        ...stored,
        currency: stored.currency ?? undefined,
        contractTerms: stored.contractTerms ?? undefined,
        guaranteeCents,
        doorSplit,
        doorSplitPct: pct,
        doorSplitAfterCents,
      },
    });
  }

  if (query.isLoading) return <div className={styles.block} data-dense={dense ? "true" : undefined}><Spinner /></div>;
  if (query.isError || !d) {
    return (
      <div className={styles.block} data-dense={dense ? "true" : undefined}>
        <h3 className={styles.blockTitle}>Settlement</h3>
        <EmptyState
          title="Couldn’t load settlement"
          description={query.error instanceof ApiError ? query.error.message : "Please try again."}
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  const f = form ?? formFromDto(d);
  const ro = settled;   // settled ⇒ every field renders read-only, but the layout stays EXACT (Ryan)
  function setField(p: Partial<FormState>) { if (!ro) setForm({ ...f, ...p }); }

  // The expense items listed in the box. Each participates in the server's `expensesTotalCents` (member pay +
  // travel legs + attributed linked days + custom lines); the box shows them individually with NO total (Ryan)
  // — the single Expenses line on the left carries the sum the net subtracts.
  const memberPay = plan.data?.data?.memberPay ?? [];
  const travelLegs = (travelQ.data?.items ?? []).filter((t) => (t.cost?.costCents ?? 0) !== 0);
  const linkedLines = linkedDays.data?.days ?? [];
  const customLines = expensesQ.data?.items ?? [];
  // The show-day per diem: rate × the show's members, with ShowPnl's one-person floor so this row and the net's
  // Expenses line agree (ART-296). memberCount is the server's count of the member list.
  const perDiemRate = plan.data?.data?.perDiemCents ?? 0;
  const perDiemHeads = Math.max(1, plan.data?.data?.memberCount ?? 0);
  const perDiemCents = perDiemRate * perDiemHeads;
  const hasAnyExpense =
    memberPay.length + travelLegs.length + linkedLines.length + customLines.length > 0 || perDiemCents > 0;

  const via = viaLabel(f);

  return (
    <div className={styles.block} data-dense={dense ? "true" : undefined}>
      <div className={styles.head}>
        <h3 className={styles.blockTitle}>Settlement</h3>
        {/* The SETTLEMENT-WITH-VENUE chip (Ryan, ART-241): the worksheet's own status alone — "In progress" or
            "Settled" — never the show's open/completed lifecycle. Flipped by "Mark settled" (the net card's
            button + the DOS card) and the audited Reopen. */}
        <span className={styles.status} data-status={d.status ?? "in_progress"} data-testid="settlement-status">
          {settlementStatusLabel(d.status)}
        </span>
      </div>

      <div className={styles.columns}>
        {/* LEFT — the real net with all its line items on top, then the door/vip/terms inputs. */}
        <div className={styles.column}>
          <NetStack
            d={d}
            via={via}
            action={
              /* "Mark settled" WITH THE VENUE (Ryan, ART-241) — the desktop twin of the DOS card's button, same
                 `useFinalizeSettlement`. Shown only while unsettled; once settled the card is read-only and the
                 bottom-row "Reopen show" is the inverse. Direct action (no confirm), matching DOS. money:full |
                 money:per_show is already the panel's gate, so no extra capability check is needed. */
              !settled ? (
                <Button
                  variant="accent"
                  size={dense ? "xs" : "sm"}
                  loading={finalize.isPending}
                  onClick={() => finalize.mutate(showId)}
                  data-testid="mark-settled"
                >
                  Mark settled
                </Button>
              ) : undefined
            }
          />

          <fieldset className={styles.fieldset}>
            <FieldsetHead title="Door" total={`${money(grossDoorCents(f))} gross`} />
            <div className={styles.grid}>
              <NumField label="Presale sold" value={f.presaleSold} readOnly={ro} onChange={(v) => setField({ presaleSold: v })} />
              <MoneyIn label="Pre $" value={f.presalePrice} readOnly={ro} onChange={(v) => setField({ presalePrice: v })} />
              <NumField label="Walk-up sold" value={f.walkupSold} readOnly={ro} onChange={(v) => setField({ walkupSold: v })} />
              <MoneyIn label="Walk $" value={f.walkupPrice} readOnly={ro} onChange={(v) => setField({ walkupPrice: v })} />
            </div>
          </fieldset>

          <fieldset className={styles.fieldset}>
            <FieldsetHead title="VIP & comps" />
            <div className={clsx(styles.grid, styles.grid3)}>
              <NumField label="VIP sold" value={f.vipCount} readOnly={ro} onChange={(v) => setField({ vipCount: v })} />
              <MoneyIn label="VIP $" value={f.vipPrice} readOnly={ro} onChange={(v) => setField({ vipPrice: v })} />
              <ReadField label="Comps" testId="comps">{d.compsCount}</ReadField>
            </div>
          </fieldset>

          {/* Splits → TERMS (Ryan). The deal — one authority (`show_finances`); the merch cut lives in the
              show editor now, so it is no longer a section here (only the resulting cut is a net line item). */}
          <fieldset className={styles.fieldset}>
            <FieldsetHead title="Terms" />
            <div className={styles.grid}>
              <MoneyIn label="Guarantee" value={f.guarantee} readOnly={ro} onChange={(v) => setField({ guarantee: v })} />
              <MoneyIn label="Deposit" value={f.deposit} readOnly={ro} onChange={(v) => setField({ deposit: v })} />
              <PctIn label="Door split" value={f.doorSplitPct} readOnly={ro} onChange={(v) => setField({ doorSplitPct: v })} />
              <MoneyIn label="Kicks in after" value={f.doorSplitAfter} readOnly={ro} onChange={(v) => setField({ doorSplitAfter: v })} />
            </div>
          </fieldset>
        </div>

        {/* RIGHT — ONE tall Expenses box, the height of the left column, scrolling when it needs to. Add-expense
            sits top-right; there is NO total here — the net's Expenses line carries the sum (Ryan). */}
        <div className={styles.column}>
          <section className={styles.expensesBox}>
            <div className={styles.expensesBoxHead}>
              <span className={styles.fieldsetTitle}>Expenses</span>
              {!ro && (
                <Button variant="ghost" size={dense ? "xs" : "sm"} onClick={() => setExpensesOpen(true)}>
                  Add expense
                </Button>
              )}
            </div>
            <div className={styles.expensesList} data-testid="expenses-list">
              {!hasAnyExpense && <p className={styles.expensesEmpty}>No expenses on this show yet.</p>}
              {memberPay.map((m: ShowMemberPayLineDto, i: number) => (
                <ExpenseItem key={`mp-${m.personId}-${i}`} label="Member pay" value={money(m.amountCents)} />
              ))}
              {perDiemCents > 0 && (
                <ExpenseItem label={`Per diem · ${perDiemHeads} × ${money(perDiemRate)}`} value={money(perDiemCents)} />
              )}
              {travelLegs.map((t: TravelItemDto) => (
                <ExpenseItem key={`tr-${t.travelItemId}`} label={travelLabel(t)} value={money(t.cost?.costCents)} />
              ))}
              {linkedLines.map((l: LinkedDayLine) => (
                <ExpenseItem key={`ld-${l.tourDayId}`} label={l.label ?? (l.kind === "travel" ? "Travel day" : "Day off")}
                  value={money((l.perDiemCents ?? 0) + (l.payCents ?? 0))} />
              ))}
              {customLines.map((l: ExpenseLineDto) => (
                <ExpenseItem key={`cl-${l.lineId}`}
                  label={l.addedByName ? `${l.label || "Expense"} · ${l.addedByName}` : (l.label || "Expense")}
                  value={money(l.amountCents)} />
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* The bottom row carries the ONE settled-state action: Reopen show (audited, money:full). Nothing sits
          here while in progress — edits autosave, and settling is done in Outstanding Payments. */}
      {settled && canFull && (
        <div className={styles.bottomRow}>
          <ReopenShowButton showId={showId} variant="ghost" size={dense ? "xs" : "sm"} />
        </div>
      )}

      {expensesOpen && <ShowExpensesModal showId={showId} onClose={() => setExpensesOpen(false)} />}
    </div>
  );
}

// ---- the net stack: the real net + its line items (Ryan: "all line items on the left") ---------------------

/**
 * NET TO ARTIST — the headline, then the line items that produce it, all on the left. Every figure is the
 * SERVER's (the panel never re-derives the net): `headlineTakeCents + merchGrossCents − merchCutOwedCents −
 * expensesTotalCents = netToArtistCents` (ART-290: merch sales are income), so the rows provably sum to the
 * total. A line item that is $0 is omitted (Ryan: "if there is no door share, it shouldn't show as a line item").
 */
function NetStack({ d, via, action }: { d: SettlementDto; via: string; action?: ReactNode }) {
  const net = d.netToArtistCents ?? 0;
  const headline = d.headlineTakeCents ?? 0;
  const merchGross = d.merchGrossCents ?? 0;
  const merchCut = d.merchCutOwedCents ?? 0;
  const expenses = d.expensesTotalCents ?? 0;
  const [dollars, cents] = money(net).split(".");

  return (
    <div className={styles.netStrip} data-testid="net-strip">
      <div className={styles.netMain}>
        {/* The label row carries the "Mark settled" button top-right (Ryan, ART-241). */}
        <div className={styles.netHead}>
          <span className={styles.netLabel}>Net to artist</span>
          {action}
        </div>
        <span className={styles.netValue} data-testid="net-to-artist">
          {dollars}
          <span className={styles.netCents}>.{cents}</span>
        </span>
        <span className={styles.netSub}>Via <b>{via}</b></span>
      </div>

      <div className={styles.netLines}>
        {/* The deal income (the winning basis), the merch sales, then the deductions, then NET on a rule. The
            label names which side of "vs guarantee" paid; the door-share line only appears when a split won. */}
        <NetLine label={via === "door split" ? "Door share" : "Guarantee"} value={money(headline)} />
        {merchGross > 0 && <NetLine label="Merch" value={`+${money(merchGross)}`} />}
        {merchCut > 0 && <NetLine label="Merch cut" value={`−${money(merchCut)}`} negative />}
        {expenses > 0 && <NetLine label="Expenses" value={`−${money(expenses)}`} negative />}
        <NetLine label="Net" value={money(net)} total />
      </div>
    </div>
  );
}

function NetLine({ label, value, negative, total }: { label: string; value: string; negative?: boolean; total?: boolean }) {
  return (
    <div className={styles.netLine} data-total={total || undefined}>
      <span className={styles.netLineLabel}>{label}</span>
      <span className={styles.netLineValue} data-negative={negative || undefined}>{value}</span>
    </div>
  );
}

/** A travel leg's one-line label — `Flight: {airline} {flightNumber}` / `Hotel: {property}` / `Ground`. Mirrors
 *  the shared server/editor spelling (a colon), so the Expenses box reads like the editor's §09 Travel list. */
function travelLabel(t: TravelItemDto): string {
  if (t.kind === "hotel") return `Hotel: ${t.propertyName ?? ""}`.trim().replace(/:\s*$/, ": —");
  if (t.kind === "ground") return "Ground";
  return `Flight: ${[t.airline, t.flightNumber].filter(Boolean).join(" ")}`.trim().replace(/:\s*$/, ": —");
}

function ExpenseItem({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.expenseItem}>
      <span className={styles.expenseItemLabel}>{label}</span>
      <span className={styles.expenseItemValue}>{value}</span>
    </div>
  );
}

// ---- Add custom expenses (show_expense_lines) ------------------------------------------------------------

function ShowExpensesModal({ showId, onClose }: { showId: string; onClose: () => void }) {
  const query = useExpenses(showId);
  const update = useUpdateExpenses();
  const [rows, setRows] = useState<ExpenseLineDraft[]>([{ label: "", amount: "", needsReimbursement: true }]);
  const filled = rows.filter((r) => r.label.trim() !== "");

  function add() {
    // APPEND: the modal never shows the stored lines, so a full-replace PUT must carry them forward.
    const existing = (query.data?.items ?? []).map((l) => ({
      label: l.label ?? "", amountCents: l.amountCents ?? 0, needsReimbursement: l.needsReimbursement ?? true,
      lineId: l.lineId,
    }));
    const additions = filled.map((r) => ({
      label: r.label.trim(), amountCents: strToCents(r.amount) ?? 0, needsReimbursement: r.needsReimbursement,
    }));
    update.mutate({ showId, body: { items: [...existing, ...additions] } }, { onSuccess: () => onClose() });
  }

  return (
    <ActionModal
      title={filled.length > 1 ? "Add expenses" : "Add expense"}
      size="md"
      confirmLabel={filled.length > 1 ? `Add ${filled.length} expenses` : "Add expense"}
      onConfirm={add}
      onCancel={onClose}
      loading={update.isPending}
      confirmDisabled={filled.length === 0 || !query.data}
    >
      <ExpenseLinesList rows={rows} onChange={setRows} addLabel="Add another" empty="No expenses to add yet." />
    </ActionModal>
  );
}

// ---- form plumbing ---------------------------------------------------------------------------------------

type FormState = {
  presaleSold: string; presalePrice: string; walkupSold: string; walkupPrice: string;
  vipCount: string; vipPrice: string;
  guarantee: string; deposit: string; doorSplitPct: string; doorSplitAfter: string;
  /** CARRIED, not edited — a whole-document PUT deletes a field it does not round-trip, so the deposit's
   *  received stamp (set in the editor) rides through the autosave untouched. */
  depositReceivedOn: string;
  /** CARRIED — the merch section moved to the editor, but the settlement PUT is whole-document, so the merch
   *  gross + rates must round-trip or a door edit would wipe them (and the derived cut with them). */
  merchGross: string; merchSoftGoodsPct: string; merchRecordedPct: string;
};

function formFromDto(d: SettlementDto): FormState {
  return {
    presaleSold: d.presaleSold == null ? "" : String(d.presaleSold),
    presalePrice: centsToStr(d.presalePriceCents),
    walkupSold: d.walkupSold == null ? "" : String(d.walkupSold),
    walkupPrice: centsToStr(d.walkupPriceCents),
    vipCount: d.vipCount == null ? "" : String(d.vipCount),
    vipPrice: centsToStr(d.vipPriceCents),
    guarantee: centsToStr(d.guaranteeCents),
    deposit: centsToStr(d.depositCents),
    depositReceivedOn: d.depositReceivedOn ?? "",
    doorSplitPct: d.doorSplitPct == null ? "" : String(d.doorSplitPct),
    doorSplitAfter: centsToStr(d.doorSplitAfterCents),
    merchGross: centsToStr(d.merchGrossCents),
    merchSoftGoodsPct: d.merchSoftGoodsPct == null ? "" : String(d.merchSoftGoodsPct),
    merchRecordedPct: d.merchRecordedPct == null ? "" : String(d.merchRecordedPct),
  };
}

function toScalars(f: FormState) {
  return {
    presaleSold: strToInt(f.presaleSold), presalePriceCents: strToCents(f.presalePrice),
    walkupSold: strToInt(f.walkupSold), walkupPriceCents: strToCents(f.walkupPrice),
    vipCount: strToInt(f.vipCount), vipPriceCents: strToCents(f.vipPrice),
    // guaranteeCents / doorSplitPct / doorSplitAfterCents are NOT sent — the DEAL's authority is
    // `show_finances`, written by `saveDeal()`. The merch fields round-trip untouched (edited in the editor).
    depositCents: strToCents(f.deposit),
    depositReceivedOn: f.depositReceivedOn.trim() === "" ? undefined : f.depositReceivedOn,
    merchGrossCents: strToCents(f.merchGross),
    merchSoftGoodsPct: f.merchSoftGoodsPct.trim() === "" ? undefined : Number(f.merchSoftGoodsPct),
    merchRecordedPct: f.merchRecordedPct.trim() === "" ? undefined : Number(f.merchRecordedPct),
  };
}

/** Which side of "vs guarantee" paid, from what is currently typed — names the deal-income line + the sub. */
function viaLabel(f: FormState): string {
  const guarantee = strToCents(f.guarantee) ?? 0;
  return doorShareCents(f) > guarantee ? "door split" : "guarantee";
}

// Field renders its own <input>; the unit rides inside the box (`affixStart`/`affixEnd`). A read-only field
// renders as a display cell so a settled panel keeps the EXACT layout without an editable control.
function MoneyIn({ label, value, onChange, readOnly }: { label: string; value: string; onChange: (v: string) => void; readOnly?: boolean }) {
  if (readOnly) return <ReadField label={label}>{value.trim() === "" ? money(0) : money(Math.round(Number(value) * 100))}</ReadField>;
  return <Field label={label} density="compact" affixStart="$" type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={value} onChange={(e) => onChange(e.target.value)} />;
}
function NumField({ label, value, onChange, readOnly }: { label: string; value: string; onChange: (v: string) => void; readOnly?: boolean }) {
  if (readOnly) return <ReadField label={label}>{value.trim() === "" ? "0" : value}</ReadField>;
  return <Field label={label} density="compact" type="number" inputMode="numeric" min="0" step="1" placeholder="0" value={value} onChange={(e) => onChange(e.target.value)} />;
}
function PctIn({ label, value, onChange, readOnly }: { label: string; value: string; onChange: (v: string) => void; readOnly?: boolean }) {
  if (readOnly) return <ReadField label={label}>{value.trim() === "" ? "0%" : `${value}%`}</ReadField>;
  return <Field label={label} density="compact" affixEnd="%" type="number" inputMode="decimal" min="0" max="100" step="0.01" placeholder="0" value={value} onChange={(e) => onChange(e.target.value)} />;
}

function FieldsetHead({ title, total }: { title: string; total?: string }) {
  return (
    <legend>
      <span className={styles.fieldsetHead}>
        <span className={styles.fieldsetTitle}>{title}</span>
        {total != null && <span className={styles.fieldsetTotal}>{total}</span>}
      </span>
    </legend>
  );
}

/** The gross door the Door header states — GA + walk-up + VIP, from what is CURRENTLY typed. A DISPLAY echo of
 *  `SettlementCalculator`'s own `grossDoor` term (VIP is part of the door); never an authority. */
function grossDoorCents(f: FormState): number {
  const n = (s: string) => { const v = Number(s.trim()); return Number.isFinite(v) ? v : 0; };
  const cents = (s: string) => Math.round(n(s) * 100);
  return n(f.presaleSold) * cents(f.presalePrice) + n(f.walkupSold) * cents(f.walkupPrice) + n(f.vipCount) * cents(f.vipPrice);
}

/** The door share as currently typed — a DISPLAY echo used only to name which side of "vs guarantee" paid. */
function doorShareCents(f: FormState): number {
  const pct = Number(f.doorSplitPct.trim());
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  const splittable = Math.max(0, grossDoorCents(f) - (strToCents(f.doorSplitAfter) ?? 0));
  return Math.round((splittable * pct) / 100);
}

/** A read-only labelled value (comps, and every field when settled). */
function ReadField({ label, testId, children, className }: { label: string; testId?: string; children: ReactNode; className?: string }) {
  return (
    <div className={[styles.readField, className].filter(Boolean).join(" ")}>
      <span className={styles.readLabel}>{label}</span>
      <span className={styles.readonly} data-testid={testId}>{children}</span>
    </div>
  );
}
